import { useState, useEffect } from "react";
import { Loader2, Users, Send, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface Friend {
  id: string;
  user_id: string;
  friend_id: string;
  friend_username: string;
}

interface InviteFriendsModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
}

const InviteFriendsModal = ({ open, onClose, sessionId }: InviteFriendsModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  const [sendingTo, setSendingTo] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    
    const fetchFriends = async () => {
      setLoading(true);
      const { data } = await (supabase.from("friendships") as any)
        .select("*")
        .eq("status", "accepted")
        .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`);
      
      const otherIds = new Set<string>();
      (data || []).forEach((f: any) => {
        otherIds.add(f.user_id === user.id ? f.friend_id : f.user_id);
      });
      
      let profileMap: Record<string, string> = {};
      if (otherIds.size > 0) {
        const { data: profiles } = await supabase.rpc("get_public_profiles", { _user_ids: Array.from(otherIds) });
        (profiles || []).forEach((p: any) => {
          profileMap[p.id] = p.username || "Unknown";
        });
      }
      
      const enriched = (data || []).map((f: any) => {
        const otherId = f.user_id === user.id ? f.friend_id : f.user_id;
        return { ...f, friend_username: profileMap[otherId] || "Unknown" };
      });
      
      const { data: existingInvites } = await (supabase.from("group_invites") as any)
        .select("to_user_id, status")
        .eq("session_id", sessionId)
        .eq("from_user_id", user.id);
      
      const alreadyInvited = new Set<string>();
      (existingInvites || []).forEach((inv: any) => {
        alreadyInvited.add(inv.to_user_id);
      });
      setInvitedIds(alreadyInvited);
      
      setFriends(enriched);
      setLoading(false);
    };
    
    fetchFriends();
  }, [open, user, sessionId]);

  const sendInvite = async (friendId: string) => {
    if (!user) return;
    setSendingTo(friendId);
    try {
      const { error } = await (supabase.from("group_invites") as any)
        .insert({
          session_id: sessionId,
          from_user_id: user.id,
          to_user_id: friendId,
          status: "pending",
        });
      if (error) throw error;
      setInvitedIds((prev) => new Set(prev).add(friendId));
      toast.success(t("inviteFriends.inviteSent"));
    } catch (e: any) {
      toast.error(e.message || t("inviteFriends.inviteFailed"));
    } finally {
      setSendingTo(null);
    }
  };

  const getFriendUserId = (friend: Friend) => {
    return friend.user_id === user?.id ? friend.friend_id : friend.user_id;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border rounded-2xl max-w-sm mx-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> {t("inviteFriends.title")}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            {t("inviteFriends.subtitle")}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-2 mt-2 max-h-64 overflow-y-auto">
          {loading ? (
            <div className="text-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto" />
            </div>
          ) : friends.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">
              {t("inviteFriends.noFriends")}
            </p>
          ) : (
            friends.map((friend) => {
              const friendUserId = getFriendUserId(friend);
              const isInvited = invitedIds.has(friendUserId);
              return (
                <div
                  key={friend.id}
                  className="flex items-center gap-3 p-3 rounded-xl bg-secondary border border-border"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-iris flex items-center justify-center">
                    <span className="text-xs font-bold text-primary-foreground">
                      {(friend.friend_username || "?")[0].toUpperCase()}
                    </span>
                  </div>
                  <p className="flex-1 text-sm font-medium text-foreground truncate">
                    @{friend.friend_username}
                  </p>
                  {isInvited ? (
                    <span className="flex items-center gap-1 text-xs text-primary">
                      <Check className="w-3 h-3" /> {t("inviteFriends.sent")}
                    </span>
                  ) : (
                    <button
                      onClick={() => sendInvite(friendUserId)}
                      disabled={sendingTo === friendUserId}
                      className="px-3 py-1.5 rounded-lg bg-primary/15 text-primary text-xs font-medium hover:bg-primary/25 transition-colors disabled:opacity-50"
                    >
                      {sendingTo === friendUserId ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Send className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteFriendsModal;
